import { MegaphoneIcon, PinIcon } from "lucide-react";
import { InformationText } from "@/components/app/information-text";
import { Card } from "@/components/ui/card";
import { useT } from "@/i18n";
import { eventWhen } from "@/i18n/format";
import { useCalendarNow } from "@/lib/use-calendar-now";
import {
  activeAnnouncements,
  type TournamentAnnouncement,
} from "../../../shared/tournament-information";
import { wallClock } from "../../../shared/wall-clock";

export function TournamentAnnouncements({
  notices,
  timezone,
}: {
  notices: TournamentAnnouncement[];
  timezone: string | undefined;
}) {
  const t = useT();
  const now = useCalendarNow();
  const active = activeAnnouncements(notices, timezone, now.getTime());
  if (!active.length) return null;
  return (
    <Card className="gap-0 p-0">
      <h2 className="flex items-center gap-3 border-b px-5 py-4 text-lg font-semibold adm:px-6">
        <MegaphoneIcon className="size-5 shrink-0 text-brand-ink" aria-hidden="true" />
        {t("announcements.title")}
      </h2>
      <div className="divide-y">
        {active.map((notice) => (
          <article key={notice.id} className="space-y-3 px-5 py-5 adm:px-6">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {notice.pinned && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-ink">
                  <PinIcon className="size-3.5" aria-hidden="true" />
                  {t("announcements.pinned")}
                </span>
              )}
              {notice.updatedAt !== undefined && (
                <time
                  dateTime={new Date(notice.updatedAt).toISOString()}
                  className="text-xs text-muted-foreground"
                >
                  {t("announcements.updated", {
                    when: eventWhen(wallClock(timezone ?? "UTC", notice.updatedAt) ?? ""),
                  })}
                  {!timezone && " UTC"}
                </time>
              )}
            </div>
            <h3 className="break-words text-base font-semibold">{notice.title}</h3>
            <InformationText text={notice.text} />
            {notice.expiresAt && (
              <p className="text-xs text-muted-foreground">
                {t("announcements.until", { when: eventWhen(notice.expiresAt) })}
              </p>
            )}
          </article>
        ))}
      </div>
    </Card>
  );
}
